import React, { PropsWithChildren } from 'react'
import { Helmet } from 'react-helmet-async'

export interface SeoHeadProps {
  // Basic meta tags
  title: string
  description: string

  // Optional meta tags
  canonical?: string
  keywords?: string
  author?: string

  // Open Graph tags
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  ogImageAlt?: string
  ogImageWidth?: string
  ogImageHeight?: string
  ogType?: 'website' | 'article' | 'product' | 'hotel'
  ogUrl?: string
  ogSiteName?: string
  ogLocale?: string

  // Twitter Card tags
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player'
  twitterSite?: string
  twitterCreator?: string
  twitterTitle?: string
  twitterDescription?: string
  twitterImage?: string
  twitterImageAlt?: string

  // Additional tags
  alternateLanguages?: Array<{ lang: string; href: string }>
  noindex?: boolean
  nofollow?: boolean

  // Structured data
  structuredData?: object | object[]
}

/**
 * Enhanced SEO Head component with comprehensive meta tag support
 */
export const SeoHead: React.FC<PropsWithChildren<SeoHeadProps>> = ({
  title,
  description,
  canonical,
  keywords,
  author,
  ogTitle,
  ogDescription,
  ogImage,
  ogImageAlt,
  ogImageWidth,
  ogImageHeight,
  ogType = 'website',
  ogUrl,
  ogSiteName = '北海道ボールパークFビレッジ',
  ogLocale = 'ja_JP',
  twitterCard = 'summary_large_image',
  twitterSite,
  twitterCreator,
  twitterTitle,
  twitterDescription,
  twitterImage,
  twitterImageAlt,
  alternateLanguages,
  noindex,
  nofollow,
  structuredData,
  children,
}) => {
  const robotsContent = [noindex && 'noindex', nofollow && 'nofollow']
    .filter(Boolean)
    .join(', ')

  return (
    <React.Fragment>
      <Helmet prioritizeSeoTags>
        {/* Basic Meta Tags */}
        <title>{title}</title>
        <meta name="description" content={description} />
        {keywords && <meta name="keywords" content={keywords} />}
        {author && <meta name="author" content={author} />}
        {canonical && <link rel="canonical" href={canonical} />}
        {robotsContent && <meta name="robots" content={robotsContent} />}

        {/* Open Graph Tags */}
        <meta property="og:title" content={ogTitle || title} />
        <meta
          property="og:description"
          content={ogDescription || description}
        />
        <meta property="og:type" content={ogType} />
        <meta property="og:site_name" content={ogSiteName} />
        <meta property="og:locale" content={ogLocale} />
        {ogUrl && <meta property="og:url" content={ogUrl} />}
        {ogImage && <meta property="og:image" content={ogImage} />}
        {ogImage && ogImageAlt && (
          <meta property="og:image:alt" content={ogImageAlt} />
        )}
        {ogImage && ogImageWidth && (
          <meta property="og:image:width" content={ogImageWidth} />
        )}
        {ogImage && ogImageHeight && (
          <meta property="og:image:height" content={ogImageHeight} />
        )}

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content={twitterCard} />
        {twitterSite && <meta name="twitter:site" content={twitterSite} />}
        {twitterCreator && (
          <meta name="twitter:creator" content={twitterCreator} />
        )}
        <meta name="twitter:title" content={twitterTitle || ogTitle || title} />
        <meta
          name="twitter:description"
          content={twitterDescription || ogDescription || description}
        />
        {/* react-helmet-async only reads tags that are *direct* children of
            <Helmet>; wrapping these in a Fragment silently drops them, so emit
            each <meta> as a direct child instead. */}
        {(twitterImage || ogImage) && (
          <meta name="twitter:image" content={twitterImage || ogImage} />
        )}
        {(twitterImage || ogImage) && (twitterImageAlt || ogImageAlt) && (
          <meta
            name="twitter:image:alt"
            content={twitterImageAlt || ogImageAlt}
          />
        )}

        {/* Alternate Language Links */}
        {alternateLanguages?.map(({ lang, href }) => (
          <link key={lang} rel="alternate" hrefLang={lang} href={href} />
        ))}

        {/* Structured Data */}
        {structuredData && (
          <script type="application/ld+json">
            {JSON.stringify(
              Array.isArray(structuredData) ? structuredData : [structuredData]
            )}
          </script>
        )}
      </Helmet>
      {children}
    </React.Fragment>
  )
}
